import { useCallback, useEffect, useMemo, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import Keyboard, { type KeyTone } from '../../shared/ui/Keyboard';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import {
  normalize,
  type EmojiAnagramLevel,
  type NormalizedEmojiAnagram,
} from './types';
import './emojiAnagram.css';

export default function EmojiAnagramGame() {
  const [data, setData] = useState<{
    puzzle: NormalizedEmojiAnagram;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<EmojiAnagramLevel>('emojiAnagram')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Emoji Anagram yet.');
          return;
        }
        const puzzle = normalize(res.data);
        if (!puzzle) {
          setLoadError('Today\'s Emoji Anagram puzzle is malformed.');
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="emojiAnagram" />;
  if (!data) {
    return (
      <GameShell gameId="emojiAnagram">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('emojiAnagram', data.dateKey)) {
    return (
      <FinishedDaily gameId="emojiAnagram" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <EmojiAnagramPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function EmojiAnagramPlay({
  puzzle,
  dateKey,
  isToday,
}: {
  puzzle: NormalizedEmojiAnagram;
  dateKey: string;
  isToday: boolean;
}) {
  const { restored, save } = useDailySnapshot<{
    revealed: string[];
    wrong: string[];
    hintShown: boolean;
    mistakes: number;
  }>('emojiAnagram', dateKey);

  const [revealed, setRevealed] = useState<Set<string>>(
    () => new Set(restored?.revealed ?? []),
  );
  const [wrong, setWrong] = useState<Set<string>>(() => new Set(restored?.wrong ?? []));
  const [hintShown, setHintShown] = useState(restored?.hintShown ?? false);
  const [mistakes, setMistakes] = useState(restored?.mistakes ?? 0);
  const [shake, setShake] = useState(false);
  const [completed, setCompleted] = useState(false);

  usePersistedState(
    save,
    { revealed: [...revealed], wrong: [...wrong], hintShown, mistakes },
    !completed,
  );

  useEffect(() => {
    if (completed) recordOutcome('emojiAnagram', dateKey, 'won', { subtitle: 'Decoded it.' });
  }, [completed, dateKey]);

  const checkCompletion = useCallback(
    (set: Set<string>) => {
      if ([...puzzle.uniqueLetters].every((ch) => set.has(ch))) {
        setCompleted(true);
      }
    },
    [puzzle.uniqueLetters],
  );

  const guess = useCallback(
    (letter: string) => {
      if (completed) return;
      if (revealed.has(letter) || wrong.has(letter)) return;
      if (puzzle.uniqueLetters.has(letter)) {
        const next = new Set(revealed).add(letter);
        setRevealed(next);
        checkCompletion(next);
      } else {
        setWrong((prev) => new Set(prev).add(letter));
        setMistakes((m) => m + 1);
        setShake(true);
        setTimeout(() => setShake(false), 450);
      }
    },
    [completed, revealed, wrong, puzzle.uniqueLetters, checkCompletion],
  );

  const useHint = useCallback(() => {
    if (completed || hintShown) return;
    setHintShown(true);
    const next = new Set(revealed);
    for (const ch of puzzle.answerStripped) {
      if (!revealed.has(ch) && !wrong.has(ch)) {
        next.add(ch);
        break;
      }
    }
    setRevealed(next);
    checkCompletion(next);
  }, [completed, hintShown, revealed, wrong, puzzle.answerStripped, checkCompletion]);

  const tones: Record<string, KeyTone | undefined> = useMemo(() => {
    const out: Record<string, KeyTone | undefined> = {};
    for (const ch of revealed) out[ch] = 'correct';
    for (const ch of wrong) out[ch] = 'absent';
    return out;
  }, [revealed, wrong]);

  const disabled = useMemo(() => {
    const set = new Set<string>();
    for (const ch of revealed) set.add(ch);
    for (const ch of wrong) set.add(ch);
    return set;
  }, [revealed, wrong]);

  return (
    <GameShell
      gameId="emojiAnagram"
      subtitle={`${formatDateLabel(dateKey, isToday)} • Mistakes: ${mistakes}`}
    >
      <div className="ea-board">
        <div className="ea-emojis">
          {puzzle.emojis.map((e, i) => (
            <span key={i} className="ea-emoji">
              {e}
            </span>
          ))}
        </div>

        <div className={'ea-mistakes' + (mistakes > 0 ? ' is-warn' : '')}>
          Mistakes: {mistakes}
        </div>

        {hintShown && <div className="ea-hint">{puzzle.hint}</div>}

        <div className={'ea-slots' + (shake ? ' is-shake' : '')}>
          {puzzle.wordLengths.map((len, wIdx) => {
            let offset = 0;
            for (let i = 0; i < wIdx; i++) offset += puzzle.wordLengths[i];
            return (
              <div key={wIdx} className="ea-word">
                {Array.from({ length: len }).map((_, i) => {
                  const ch = puzzle.answerStripped[offset + i];
                  const show = revealed.has(ch);
                  return (
                    <div key={i} className={'ea-slot' + (show ? ' is-revealed' : '')}>
                      {show ? ch : ''}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <Keyboard
          letterTones={tones}
          disabledLetters={disabled}
          onLetter={guess}
          showDelete={false}
          showSubmit={false}
          onHint={useHint}
          hintEnabled={!hintShown && !completed}
        />

        {completed && (
          <DailyDoneOverlay
            dateKey={dateKey}
            isToday={isToday}
            subtitle={`Solved "${puzzle.answer}"${
              mistakes > 0 ? ` with ${mistakes} mistakes.` : ' without a single mistake!'
            }`}
          />
        )}
      </div>
    </GameShell>
  );
}
